import Clear from "@mui/icons-material/Clear";
import Refresh from "@mui/icons-material/Refresh";
import { Button, Checkbox, IconButton, Stack, Typography } from "@mui/material";
import { DataGridPro, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-pro";
import { type FC, useCallback, useMemo, useState } from "react";
import { getFlags } from "../../../../../../server/optimizely";
import "./debug.css";

type Row = {
  id: number;
  flag: string;
  enabled: boolean;
  source: string;
};

export const DebugFlagsGrid: FC = () => {
  const [flags, setFlags] = useState(getFlags());

  const refreshFlags = useCallback(() => {
    setFlags(getFlags());
  }, []);

  const toggleFlag = useCallback(
    (flag: string) => {
      window.ff?.toggle(flag);
      refreshFlags();
    },
    [refreshFlags],
  );

  const clearOverrides = useCallback(() => {
    window.ff?.clear();
    refreshFlags();
  }, [refreshFlags]);

  const clearOverride = useCallback(
    (flag: string) => {
      window.ff?.reset(flag);
      refreshFlags();
    },
    [refreshFlags],
  );

  const rows: Row[] = useMemo(
    () =>
      Object.entries(flags).map(([key, { enabled, source }], index) => ({
        id: index,
        flag: key,
        enabled,
        source,
      })),
    [flags],
  );

  const columns = useMemo(() => {
    const cols: GridColDef<Row>[] = [
      { field: "flag", headerName: "Flag", flex: 1 },
      {
        field: "enabled",
        headerName: "Enabled",
        flex: 0.5,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<Row>) => (
          <Checkbox checked={row.enabled} onChange={() => toggleFlag(row.flag)} size="small" />
        ),
      },
      { field: "source", headerName: "Source", flex: 0.7 },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: GridRenderCellParams<Row>) =>
          row.source === "Override" ? (
            <Button
              size="small"
              startIcon={<Clear />}
              onClick={() => clearOverride(row.flag)}
              data-analytics-id={`debug-clear-override-${row.flag}`}
            >
              Clear
            </Button>
          ) : null,
      },
    ];

    return cols;
  }, [clearOverride, toggleFlag]);

  return (
    <Stack spacing={1} direction="column">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Features</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={clearOverrides} data-analytics-id="debug-clear-overrides">
            Clear Overrides
          </Button>
          <IconButton size="small" onClick={refreshFlags} data-analytics-id="debug-refresh-flags">
            <Refresh fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <DataGridPro
        rows={rows}
        getRowClassName={(params) => (params.row.source === "Override" ? "overridden-flag" : "")}
        columns={columns}
        disableRowSelectionOnClick
        initialState={{
          sorting: { sortModel: [{ field: "flag", sort: "asc" }] },
        }}
      />
    </Stack>
  );
};
